import { Link2Icon, PlusIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useWorkspace } from '@/features/workspaces';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import type { RelationEntry, RelationRowModel } from '../types';
import { relationLabel } from '../types';
import type {
  TaskRelationViewRelationType,
  TaskView,
} from '@/lib/api/generated/model';
import { RelationRow } from './RelationRow';
import { TaskRelationPicker } from './TaskRelationPicker';

export interface RelationsSectionProps {
  workspaceId: string;
  relations: RelationEntry[];
  onAdd: (
    task: TaskView,
    relationType: TaskRelationViewRelationType | undefined,
  ) => void;
  onRemove: (entry: RelationEntry) => void;
  /** Tasks the picker must not offer: already staged here. */
  excludeTaskIds: string[];
  isPending?: boolean;
  error?: string | null;
}

/**
 * Connections staged while a task is being created.
 *
 * This used to be the editor on the task detail view as well. It is not any
 * more: a saved task's connections live in the drawer's Relations tab, which can
 * also show what the task is connected to further out. This section stays for
 * the one case that tab can never serve, because there is no saved task yet and
 * therefore nothing to query or to centre a wider view on (spec 100 FR-035,
 * FR-037).
 *
 * It renders the same `RelationRow` the tab does, so a connection staged here
 * and the same connection seen after the task exists read as one thing
 * (FR-036). Nothing here talks to the backend: the caller stages, and writes
 * every staged connection once the task has been created.
 */
export function RelationsSection({
  workspaceId,
  relations,
  onAdd,
  onRemove,
  excludeTaskIds,
  isPending = false,
  error = null,
}: RelationsSectionProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  /*
    Which workflow a staged task belongs to. Worth a line on the row: staging a
    connection to a task in another workflow is exactly the case where a title
    alone is ambiguous. Read here rather than passed in, so the caller does not
    have to assemble it.
  */
  const workspace = useWorkspace(workspaceId);
  const workflowNames = new Map(
    (workspace.data?.workflows ?? []).map((w) => [w.id, w.name]),
  );

  const rows: RelationRowModel[] = relations.map((entry) => ({
    key: entry.relationId ?? entry.relatedTask.id,
    title: entry.relatedTask.title,
    context: workflowNames.get(entry.relatedTask.workflow_id) ?? null,
    label: relationLabel(entry.relationType),
    // Nothing to link to: the task being created does not exist yet, so neither
    // does an address that could show this connection in context.
    link: null,
    distance: null,
    viaTitle: null,
    onRemove: () => onRemove(entry),
  }));

  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <EyebrowLabel icon={Link2Icon}>Relations</EyebrowLabel>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Add relation"
          disabled={isPending}
          onClick={() => setPickerOpen(true)}
        >
          <PlusIcon aria-hidden="true" className="size-4" strokeWidth={1.75} />
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">No relations yet.</p>
      ) : (
        <ul className="flex min-w-0 flex-col gap-1">
          {rows.map((row) => (
            <RelationRow key={row.key} row={row} />
          ))}
        </ul>
      )}

      {error ? <p className="text-destructive text-sm">{error}</p> : null}

      {pickerOpen ? (
        <TaskRelationPicker
          workspaceId={workspaceId}
          excludeTaskIds={excludeTaskIds}
          onSelect={(task, relationType) => {
            onAdd(task, relationType);
          }}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </section>
  );
}
